import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { createPublicLead } from '../lib/publicLead';

const { FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiMessageSquare, FiCheck, FiAlertCircle } = FiIcons;

function BookingSection() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', date: '', time: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const services = ['Web Development','E-Commerce Solutions','Mobile App Development','Digital Marketing','SEO Optimization','Cloud Solutions','Cybersecurity','AI Solutions','Consulting','Other'];
  const timeSlots = ['09:00','09:30','10:00','10:30','11:00','11:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const bookingNotes = `Αίτημα ραντεβού\n\nΥπηρεσία: ${formData.service || '-'}\nΗμερομηνία: ${formData.date || '-'}\nΏρα: ${formData.time || '-'}\nΜήνυμα: ${formData.message || '-'}`;

      await createPublicLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.service ? `Ραντεβού - ${formData.service}` : 'Αίτημα ραντεβού',
        message: bookingNotes,
        source: 'booking_form'
      });

      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', date: '', time: '', message: '' });
    } catch (err) {
      console.error('Error submitting booking:', err);
      setError('Δεν ήταν δυνατή η αποστολή του αιτήματος. Παρακαλούμε δοκιμάστε ξανά.');
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="booking" className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" /></div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Το αίτημα καταχωρήθηκε!</h2>
            <p className="text-xl text-gray-600 mb-6">Δημιουργήθηκε lead στο σύστημα και θα επικοινωνήσουμε μαζί σας σύντομα.</p>
            <button onClick={() => setIsSubmitted(false)} className="text-blue-600 font-medium hover:underline">Νέο αίτημα</button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Κλείστε Δωρεάν Ραντεβού</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">Επιλέξτε την κατάλληλη ημερομηνία και ώρα για μια δωρεάν συμβουλευτική συνάντηση</p>
        </motion.div>

        <motion.div className="bg-white rounded-2xl shadow-xl overflow-hidden" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }}>
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold">Κλείστε το Ραντεβού σας</h3>
            <p className="text-blue-100 mt-2">Συμπληρώστε τα στοιχεία σας παρακάτω</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {error && <div className="mb-6 flex items-start rounded-lg border border-red-200 bg-red-50 p-4 text-red-700"><SafeIcon icon={FiAlertCircle} className="mr-2 mt-0.5 h-5 w-5" />{error}</div>}
            <div className="grid md:grid-cols-2 gap-6">
              <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiUser} className="inline w-4 h-4 mr-2" />Ονοματεπώνυμο *</label><input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Το όνομά σας" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiMail} className="inline w-4 h-4 mr-2" />Email *</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="email@example.com" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiPhone} className="inline w-4 h-4 mr-2" />Τηλέφωνο</label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Υπηρεσία που σας ενδιαφέρει</label><select name="service" value={formData.service} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg"><option value="">Επιλέξτε υπηρεσία</option>{services.map(service => <option key={service} value={service}>{service}</option>)}</select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiCalendar} className="inline w-4 h-4 mr-2" />Προτιμώμενη Ημερομηνία</label><input type="date" name="date" value={formData.date} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 border border-gray-300 rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiClock} className="inline w-4 h-4 mr-2" />Προτιμώμενη Ώρα</label><select name="time" value={formData.time} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg"><option value="">Επιλέξτε ώρα</option>{timeSlots.map(time => <option key={time} value={time}>{time}</option>)}</select></div>
            </div>
            <div className="mt-6"><label className="block text-sm font-medium text-gray-700 mb-2"><SafeIcon icon={FiMessageSquare} className="inline w-4 h-4 mr-2" />Επιπλέον Πληροφορίες</label><textarea name="message" value={formData.message} onChange={handleInputChange} rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none" /></div>
            <div className="mt-8 text-center"><motion.button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg disabled:opacity-50" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>{loading ? 'Αποστολή...' : 'Κλείστε Δωρεάν Ραντεβού'}</motion.button></div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}

export default BookingSection;